// src/lib/siteConfig.js
// ⚙️ CLEH TECH – siteConfig v3 (refinado PRO)
// - ENV centralizado (com fallbacks seguros)
// - Saneamento/formatação de telefone (BR) e links WhatsApp/e-mail
// - Utilitários: getWhatsAppUrl, getMailtoUrl, parseHours, isOfficeOpen
// - Conteúdos com linguagem mais profissional e atrativa
// - Campos opcionais (resumo, tags, destaques) pensados para a página de Serviços

/* =========================
   Ambiente / Defaults
========================= */
const ENV = {
  NAME: import.meta.env.VITE_BUSINESS_NAME ?? "CLEH TECH",
  URL: (import.meta.env.VITE_SITE_URL ?? "https://clehtech.com").replace(/\/+$/, ""),
  WHATSAPP: import.meta.env.VITE_WHATSAPP ?? "", // pode vir vazio
  EMAIL: import.meta.env.VITE_EMAIL ?? "cleh_tech@outlook.com",
};

/* =========================
   Helpers internos
========================= */
function onlyDigits(v = "") {
  return String(v).replace(/\D/g, "");
}

function getSanitizedPhone() {
  const digits = onlyDigits(ENV.WHATSAPP);
  return digits || "5511991237888";
}

function formatPhoneBR(digits) {
  const d = onlyDigits(digits);
  const local = d.startsWith("55") ? d.slice(2) : d;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return digits || "(11) 00000-0000";
}

/* =========================
   Links de contato
========================= */
export function getWhatsAppUrl(
  msg = "Olá! Vim pelo site da CLEH TECH e gostaria de um orçamento.",
  { includeUtm = true } = {}
) {
  const phone = getSanitizedPhone();
  let fullMessage = msg.trim();

  if (includeUtm) {
    // assinatura discreta e humana (sem parâmetros “estranhos”)
    fullMessage += "\n\n— Enviado pelo site da CLEH TECH";
  }

  const encoded = encodeURIComponent(fullMessage);
  return `https://wa.me/${phone}?text=${encoded}`;
}

export function getMailtoUrl(
  email = ENV.EMAIL,
  subject = "Atendimento CLEH TECH",
  body = "Olá! Preciso de ajuda."
) {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/* =========================
   Horário comercial
========================= */
export function parseHours(str = "Seg–Sex, 08h–18h; Sáb 09h–13h") {
  const map = { Dom: 0, Seg: 1, Ter: 2, Qua: 3, Qui: 4, Sex: 5, Sáb: 6, Sab: 6 };
  const ranges = {};
  const parts = String(str).split(";").map((s) => s.trim()).filter(Boolean);

  for (const p of parts) {
    const [dias, horas] = p.split(",").map((s) => s.trim());
    if (!dias || !horas) continue;
    const [ini, fim] = horas.replace(/h/g, ":").split("–").map((s) => s.trim());
    const [h1, m1] = ini.split(":").map((n) => parseInt(n || "0", 10));
    const [h2, m2] = fim.split(":").map((n) => parseInt(n || "0", 10));
    const start = h1 * 60 + (m1 || 0);
    const end = h2 * 60 + (m2 || 0);

    if (dias.includes("–")) {
      const [d1, d2] = dias.split("–").map((s) => s.trim());
      const from = map[d1], to = map[d2];
      if (from != null && to != null) {
        for (let d = from; d <= to; d++) ranges[d] = [start, end];
      }
    } else {
      const d = map[dias];
      if (d != null) ranges[d] = [start, end];
    }
  }
  return ranges;
}

export function isOfficeOpen(now = new Date(), scheduleStr) {
  const ranges = parseHours(scheduleStr);
  const d = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const r = ranges[d];
  return r ? mins >= r[0] && mins <= r[1] : false;
}

/* =========================
   Config do site
========================= */
const PHONE_DIGITS = getSanitizedPhone();
const PHONE_DISPLAY = formatPhoneBR(PHONE_DIGITS.startsWith("55") ? PHONE_DIGITS.slice(2) : PHONE_DIGITS);

export const siteConfig = {
  name: ENV.NAME,
  url: ENV.URL,

  /* --------- Contatos --------- */
  contact: {
    whatsapp: PHONE_DIGITS,         // p/ integrações
    whatsappDisplay: PHONE_DISPLAY, // p/ UI
    email: ENV.EMAIL,
    telefone: PHONE_DISPLAY,
    cidadeUF: "Ferraz de Vasconcelos - SP",
    horario: "Seg–Sex, 08h–18h; Sáb 09h–13h",
  },
  contato: undefined, // compat preenchida no final

  /* --------- Navegação --------- */
  nav: [
    { to: "/", label: "Início" },
    { to: "/servicos", label: "Serviços" },
    { to: "#contato", label: "Contato", anchor: true },
  ],

  /* --------- Tokens de design (espelhados em CSS) --------- */
  tokens: {
    colors: {
      brand: "#0ea5a1",
      brandDark: "#0b867f",
      text: "#0b1320",
      textMuted: "#4b5563",
      bg: "#ffffff",
      bgAlt: "#f7fafc",
      darkBg: "#0b1320",
      darkText: "#e5e7eb",
    },
    radius: { sm: "10px", md: "16px", lg: "24px", xl: "32px" },
    shadow: {
      md: "0 10px 30px rgba(0,0,0,.10)",
      lg: "0 18px 50px rgba(0,0,0,.12)",
    },
    spacing: {
      sectionY: "clamp(56px, 8vw, 120px)",
      gap: "clamp(16px, 3vw, 32px)",
    },
    typography: {
      heroH1: "clamp(28px, 4.5vw, 48px)",
      heroSub: "clamp(16px, 2vw, 20px)",
      btn: "clamp(14px, 1.8vw, 16px)",
    },
  },

  /* --------- Conteúdo do HERO --------- */
  hero: {
    titulo: "Suporte de TI e Soluções Digitais para sua empresa",
    subtitulo:
      "Manutenção e upgrades com diagnóstico claro, segurança essencial e desempenho otimizado. Também criamos sites e landing pages leves, focados em conversão e presença no Google.",
    ctas: {
      primario: "Falar no WhatsApp",
      secundario: "Ver serviços",
      micro: "Atendimento rápido, humano e transparente",
    },
    imagem: "/img/hero.webp",
    imagemAlt: "Equipe CLEH TECH prestando suporte técnico",
    imagemSrcSet: "/img/hero.webp 1200w, /img/hero@2x.webp 2000w, /img/hero-mobile.webp 768w",
    imagemSizes: "(max-width: 768px) 92vw, (max-width: 1280px) 50vw, 600px",
    badges: [
      { text: "Atendimento remoto e local", tone: "neutral" },
      { text: "Sites rápidos (React + Vite)", tone: "brand" },
    ],
    trust: {
      enabled: true,
      text: "Mais de 20 atendimentos concluídos na região",
      icon: "⭐",
    },
  },

  /* --------- Sobre --------- */
  sobre: {
    titulo: "Sobre a CLEH TECH",
    descricao: [
      "Somos parceiros de TI para quem precisa de estabilidade, segurança e velocidade no dia a dia. Unimos manutenção de computadores, suporte remoto e boas práticas de segurança com desenvolvimento de sites e landing pages rápidas, pensadas para ranquear e converter.",
      "Falamos a sua língua: explicamos cada etapa, sugerimos o melhor caminho e documentamos as entregas. Nosso objetivo é que sua operação funcione sem atritos — e que sua presença digital traga resultados reais.",
    ],
    bullets: [
      "Diagnóstico claro e orientação objetiva",
      "Windows, upgrades (SSD/RAM), redes e segurança essencial",
      "Sites e landing pages otimizados para Google (Core Web Vitals)",
      "Automação leve para reduzir tarefas repetitivas",
    ],
  },

  /* --------- Serviços (refinados) --------- */
  servicos: {
    titulo: "Serviços",
    resumo:
      "Suporte de TI com foco em continuidade do seu negócio: manutenção, segurança e performance. E, quando o assunto é presença digital, entregamos sites e landing pages rápidos, com SEO técnico e integrações sob medida.",
    lista: [
      {
        titulo: "Suporte Técnico Remoto",
        desc: [
          "Resolução de problemas de software, atualização de drivers e remoção de malware leve.",
          "Configuração de programas essenciais e otimizações de desempenho.",
          "Orientação por chamada/WhatsApp e acesso remoto seguro (TeamViewer/AnyDesk).",
          "Registro das ações realizadas e recomendações de prevenção.",
        ],
        destaques: [
          "Atendimento ágil, sem deslocamento",
          "Registro do atendimento",
          "Passo a passo claro",
        ],
        tags: ["Windows", "Drivers", "Limpeza lógica", "Otimizações"],
        icone: "🛠️",
        imagem: "/img/servicos/suporte_remoto.webp",
        imagemAlt: "Técnico configurando PC remotamente",
        ctaMsg: "Olá! Preciso de suporte remoto. Posso explicar o problema?",
      },
      {
        titulo: "Manutenção e Upgrades de Computadores",
        desc: [
          "Diagnóstico de hardware com testes e relatório simples.",
          "Troca de peças (SSD, RAM, fonte, cooler) com cuidado e organização.",
          "Formatação/reinstalação do Windows com backup assistido e drivers atualizados.",
          "Clonagem de disco quando aplicável para preservar seus dados.",
        ],
        destaques: [
          "Mais desempenho e estabilidade",
          "Peças compatíveis e garantidas",
          "Backup e restauração assistidos",
        ],
        tags: ["SSD", "RAM", "Backup", "Clonagem", "Windows"],
        icone: "💻",
        imagem: "/img/servicos/manutencao_tecnica.webp",
        imagemAlt: "Troca de SSD e limpeza interna",
        ctaMsg: "Olá! Quero orçamento de manutenção/upgrade. Meu equipamento é:",
      },
      {
        titulo: "Criação de Sites e Landing Pages",
        desc: [
          "Sites e LPs rápidos (React + Vite) com foco em conversão e SEO técnico.",
          "Arquitetura de conteúdo, copy clara e chamada para ação estratégica.",
          "Integrações com WhatsApp, formulários, Analytics e Search Console.",
          "Páginas responsivas, acessíveis e com Core Web Vitals em mente.",
        ],
        destaques: [
          "Performance por padrão",
          "SEO técnico e estrutura limpa",
          "Integrações e métricas",
        ],
        tags: ["Landing Page", "Institucional", "SEO", "Google"],
        icone: "🌐",
        imagem: "/img/servicos/dev.webp",
        imagemAlt: "Página institucional aberta em um notebook",
        ctaMsg: "Olá! Quero um site/landing page. Posso explicar meu objetivo?",
      },
      {
        titulo: "Automação para sua empresa",
        desc: [
          "Automação do uso de planilhas (Excel / Google Sheets), com criação de rotinas, validações, integrações e preenchimento automático para reduzir erros e retrabalho.",
          "Estruturação e padronização de fluxos de trabalho digitais para aumentar o desempenho das equipes e acelerar tarefas operacionais.",
          "Desenvolvimento de aplicações simples e sob medida para resolver problemas cotidianos da empresa (cadastros, checklists, controles internos, dashboards e formulários).",
          "Criação de fluxos automatizados entre formulários, planilhas e relatórios, transformando processos manuais em processos semi-automáticos.",
          "Reorganização de métodos de trabalho com apoio tecnológico, mapeando gargalos e propondo soluções práticas de automação.",
          "Simplificação de rotinas administrativas por meio de scripts, templates inteligentes e pequenos sistemas internos.",
        ],
        destaques: [
                      "Mais produtividade para a equipe",
                      "Processos padronizados e automatizados",
                      "Integração entre formulários e planilhas",
                      "Soluções rápidas para problemas do dia a dia"
                    ]
,
        tags: ["QR Code", "Automação", "Formulários", "Planilhas"],
        icone: "⚡",
        imagem: "/img/servicos/automacao.webp",
        imagemAlt: "Etiquetas com QR Codes organizacionais",
        ctaMsg: "Olá! Preciso de QR Codes/automação. Minha necessidade é:",
      },
     /* {
        titulo: "Segurança e Backups Essenciais",
        desc: [
          "Configuração de backups locais/nuvem com política simples de restauração.",
          "Checagens de integridade e orientações de boas práticas.",
          "Ajustes de permissões e segurança básica no Windows/rede.",
        ],
        destaques: ["Restauração descomplicada", "Rotina automatizada", "Prevenção de perdas"],
        tags: ["Backup", "Nuvem", "Segurança", "Windows"],
        icone: "🔐",
        imagem: "/img/servicos/backup.webp",
        imagemAlt: "Cadeado sobre ícones de armazenamento",
        ctaMsg: "Olá! Quero configurar backups/segurança. Minha situação atual é:",
      }*/,
    ],
  },

  /* --------- Contato --------- */
  contato: {
    titulo: "Fale com a gente",
    texto:
      "Conte rapidamente sua necessidade e retornaremos com o melhor caminho: suporte remoto imediato, visita técnica programada ou proposta para site/landing page.",
    enderecoCurto: "Ferraz de Vasconcelos/SP",
    horario: "Seg–Sex, 08h–18h; Sáb 09h–13h",
  },
};

// Compatibilidade
siteConfig.contato = { ...siteConfig.contact };

/* --------- Exports auxiliares --------- */
export const CONTACT_PHONE_DIGITS = PHONE_DIGITS;
export const CONTACT_PHONE_DISPLAY = PHONE_DISPLAY;
export const BUSINESS_NAME = ENV.NAME;
export const SITE_URL = ENV.URL;
export const BUSINESS_EMAIL = ENV.EMAIL;
